// src/components/AdMobBannerManager/AdMobBannerManager.js
import React, { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { isAndroidCapacitor } from '../../utils/platformUtils';
import { getAdMobPlacement } from '../../utils/admobPlacement';
import {
    disposeAdMobController,
    enterInFeedMode,
    ensureAdMobReady,
    hideAdMobBanner,
    showStickyBottomBanner,
} from '../../utils/admobController';

const REFRESH_MS = 60000;

const AdMobBannerManager = () => {
    const location = useLocation();
    const refreshHandleRef = useRef(null);
    const initializedRef = useRef(false);
    const opSeqRef = useRef(0);

    const stopRefresh = () => {
        if (refreshHandleRef.current) {
            clearInterval(refreshHandleRef.current);
            refreshHandleRef.current = null;
        }
    };

    const applyRoutePolicy = async (pathname) => {
        const mySeq = ++opSeqRef.current;
        const placement = getAdMobPlacement(pathname);

        try {
            if (placement === 'none') {
                stopRefresh();
                await hideAdMobBanner();
                return;
            }

            if (placement === 'bottom') {
                await showStickyBottomBanner();
                if (opSeqRef.current === mySeq) {
                    maybeStartRefresh(pathname);
                }
                return;
            }

            // infeed — InfiniteFeed slots own the banner
            stopRefresh();
            await enterInFeedMode();
        } catch (err) {
            console.error('[AdMob] route policy error:', err);
        }
    };

    const maybeStartRefresh = (pathname) => {
        if (getAdMobPlacement(pathname) !== 'bottom') {
            stopRefresh();
            return;
        }
        if (refreshHandleRef.current) return;

        refreshHandleRef.current = setInterval(async () => {
            try {
                await showStickyBottomBanner();
            } catch (err) {
                console.warn('[AdMob] refresh failed:', err);
            }
        }, REFRESH_MS);
    };

    useEffect(() => {
        if (!isAndroidCapacitor()) {
            document.documentElement.style.setProperty('--admob-bottom-inset', '0px');
            return undefined;
        }

        (async () => {
            try {
                await ensureAdMobReady();
                initializedRef.current = true;
                await applyRoutePolicy(location.pathname);
            } catch (err) {
                console.error('[AdMob] initialization error:', err);
            }
        })();

        return () => {
            stopRefresh();
            disposeAdMobController();
            document.documentElement.style.removeProperty('--admob-bottom-inset');
            hideAdMobBanner().catch(() => {});
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (!isAndroidCapacitor()) return undefined;

        const handleRotate = async () => {
            try {
                stopRefresh();
                await applyRoutePolicy(location.pathname);
            } catch (e) {
                console.warn('[AdMob] orientation handler error:', e);
            }
        };

        window.addEventListener('orientationchange', handleRotate);
        window.addEventListener('resize', handleRotate);
        return () => {
            window.removeEventListener('orientationchange', handleRotate);
            window.removeEventListener('resize', handleRotate);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.pathname]);

    useEffect(() => {
        if (!isAndroidCapacitor() || !initializedRef.current) return;

        (async () => {
            await applyRoutePolicy(location.pathname);
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.pathname]);

    return (
        <div
            style={{
                height: 'var(--admob-bottom-inset, 0px)',
                width: '100%',
                pointerEvents: 'none',
            }}
            aria-hidden
        />
    );
};

export default AdMobBannerManager;
